import React, { useState, useEffect } from 'react';
import { AlertTriangle, Scale, FileCheck, X, CheckCircle, ExternalLink, Shield } from 'lucide-react';
import api from '../services/api';

/**
 * Modal de Disclaimer Legal para Recursos con IA
 *
 * Este componente muestra el aviso legal obligatorio que el usuario
 * debe aceptar antes de solicitar un recurso asistido por IA.
 *
 * Props:
 * - isOpen: boolean - Si el modal esta abierto
 * - onClose: function - Callback al cerrar sin aceptar
 * - onAccept: function(aceptacionId) - Callback al aceptar (recibe ID de aceptacion)
 * - multaId: number - ID de la multa para la que se solicita recurso
 */
function DisclaimerLegalRecursos({ isOpen, onClose, onAccept, multaId }) {
  const [disclaimer, setDisclaimer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aceptando, setAceptando] = useState(false);
  const [aceptado, setAceptado] = useState(false);
  const [checkboxConfirmado, setCheckboxConfirmado] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      cargarDisclaimer();
    }
  }, [isOpen]);

  const cargarDisclaimer = async () => {
    try {
      setLoading(true);
      setError(null);

      // Primero verificar si ya acepto para esta multa
      if (multaId) {
        const verificacion = await api.get(`/disclaimer/verificar/${multaId}`);
        if (verificacion.data.aceptado) {
          // Ya acepto, cerrar y continuar
          onAccept(verificacion.data.aceptacion_id);
          return;
        }
      }

      // Obtener texto del disclaimer
      const response = await api.get('/disclaimer/texto');
      setDisclaimer(response.data.disclaimer);
    } catch (err) {
      console.error('Error cargando disclaimer:', err);
      setError('Error al cargar el aviso legal');
    } finally {
      setLoading(false);
    }
  };

  const handleAceptar = async () => {
    if (!checkboxConfirmado) {
      setError('Debe marcar la casilla de confirmacion');
      return;
    }

    try {
      setAceptando(true);
      setError(null);

      const response = await api.post('/disclaimer/aceptar', {
        multa_id: multaId,
        hash_verificado: disclaimer.hash
      });

      setAceptado(true);

      // Llamar callback con el ID de aceptacion
      setTimeout(() => {
        onAccept(response.data.aceptacion.id);
      }, 1000);
    } catch (err) {
      console.error('Error aceptando disclaimer:', err);
      setError(err.response?.data?.error || 'Error al registrar la aceptacion');
    } finally {
      setAceptando(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                  <Scale className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Aviso Legal</h2>
                  <p className="text-blue-100 text-sm">Servicio de Recursos con Asistencia IA</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Contenido */}
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">Cargando aviso legal...</span>
              </div>
            ) : aceptado ? (
              <div className="text-center py-12">
                <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Aviso Legal Aceptado</h3>
                <p className="text-gray-600">Puede continuar con la solicitud del recurso</p>
              </div>
            ) : (
              <>
                {/* Alerta importante */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <div className="flex gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-amber-800">Lea atentamente antes de continuar</h4>
                      <p className="text-sm text-amber-700 mt-1">
                        Este servicio utiliza inteligencia artificial para asistir en la elaboracion
                        de recursos. Es importante que comprenda las limitaciones y condiciones.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Texto del disclaimer */}
                {disclaimer && (
                  <div className="bg-gray-50 rounded-lg p-4 mb-6 font-mono text-sm whitespace-pre-wrap text-gray-700 leading-relaxed">
                    {disclaimer.texto}
                  </div>
                )}

                {/* Puntos clave */}
                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="w-5 h-5 text-blue-600" />
                      <span className="font-medium text-blue-900">Fuentes Verificadas</span>
                    </div>
                    <p className="text-sm text-blue-700">
                      Todas las citas legales provienen del BOE o CENDOJ con URLs verificables.
                    </p>
                  </div>

                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <FileCheck className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-green-900">Revision por Abogado</span>
                    </div>
                    <p className="text-sm text-green-700">
                      Todo recurso sera revisado y aprobado por un abogado colegiado.
                    </p>
                  </div>
                </div>

                {/* Checkbox de confirmacion */}
                <div className="border rounded-lg p-4 mb-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checkboxConfirmado}
                      onChange={(e) => setCheckboxConfirmado(e.target.checked)}
                      className="mt-1 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">
                      <strong>Confirmo que he leido y comprendido</strong> el aviso legal anterior.
                      Entiendo que la inteligencia artificial proporciona recomendaciones,
                      no asesoramiento juridico, y que el resultado del recurso no esta garantizado.
                    </span>
                  </label>
                </div>

                {/* Error */}
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-4">
                    {error}
                  </div>
                )}

                {/* Version */}
                {disclaimer && (
                  <p className="text-xs text-gray-400 text-center mb-4">
                    Version del documento: {disclaimer.version}
                  </p>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          {!loading && !aceptado && (
            <div className="border-t bg-gray-50 px-6 py-4">
              <div className="flex gap-3 justify-end">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleAceptar}
                  disabled={!checkboxConfirmado || aceptando}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {aceptando ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Registrando...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Acepto y Continuar
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DisclaimerLegalRecursos;
